import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/api-client";

type TimeRange = "1h" | "6h" | "24h" | "7d";
type SortKey = "name" | "deliveryRate" | "errorRate" | "avgDeliveryTime" | "messagesSent";
type SortDir = "asc" | "desc";

interface ProviderHealth {
  id: string;
  name: string;
  channel: string;
  deliveryRate: number;
  errorRate: number;
  avgDeliveryTime: number;
  messagesSent: number;
  lastError: { timestamp: string; message: string } | null;
  hourlyBars: number[];
}

interface HealthResponse {
  providers: ProviderHealth[];
  alertThresholds: { errorRate: number; deliveryRate: number };
}

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: "1h", label: "Last 1h" },
  { value: "6h", label: "Last 6h" },
  { value: "24h", label: "Last 24h" },
  { value: "7d", label: "Last 7d" },
];

const AUTO_REFRESH_MS = 60_000;

function rateColor(rate: number, isDelivery: boolean): string {
  if (isDelivery) {
    if (rate >= 95) return "text-emerald-600";
    if (rate >= 90) return "text-[var(--color-warning)]";
    return "text-[var(--color-destructive)]";
  }
  if (rate <= 5) return "text-emerald-600";
  if (rate <= 10) return "text-[var(--color-warning)]";
  return "text-[var(--color-destructive)]";
}

function barColor(value: number, max: number): string {
  const ratio = max > 0 ? value / max : 0;
  if (ratio >= 0.95) return "bg-emerald-500";
  if (ratio >= 0.8) return "bg-[var(--color-warning)]/40";
  return "bg-[var(--color-destructive)]/40";
}

function formatTime(ts: string): string {
  try { return new Intl.DateTimeFormat("default", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(ts)); }
  catch { return ts; }
}

function BarChart({ bars }: { bars: number[] }) {
  const max = Math.max(...bars, 1);
  return (
    <div className="flex items-end gap-px" style={{ height: 48 }}>
      {bars.map((value, i) => (
        <div
          key={i}
          className={`min-w-[4px] flex-1 rounded-t-sm ${barColor(value, max)}`}
          style={{ height: `${Math.max(max > 0 ? (value / max) * 100 : 0, 2)}%` }}
          title={`${value} messages`}
        />
      ))}
    </div>
  );
}

export default function ChannelHealthPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const [data, setData] = useState<HealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [errorThreshold, setErrorThreshold] = useState(10);
  const [deliveryThreshold, setDeliveryThreshold] = useState(90);
  const [isSavingThresholds, setIsSavingThresholds] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(async (range: TimeRange) => {
    setIsLoading(true);
    try {
      const res = await apiFetch<HealthResponse>(`/api/messaging/health?timeRange=${range}`);
      setData(res);
      setErrorThreshold(res.alertThresholds.errorRate);
      setDeliveryThreshold(res.alertThresholds.deliveryRate);
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth(timeRange);
    timerRef.current = setInterval(() => fetchHealth(timeRange), AUTO_REFRESH_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeRange, fetchHealth]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sorted = data
    ? [...data.providers].sort((a, b) => {
        const mul = sortDir === "asc" ? 1 : -1;
        if (sortKey === "name") return mul * a.name.localeCompare(b.name);
        return mul * ((a[sortKey] as number) - (b[sortKey] as number));
      })
    : [];

  const saveThresholds = async () => {
    setIsSavingThresholds(true);
    try { await apiFetch("/api/messaging/health/thresholds", { method: "PUT", body: JSON.stringify({ errorRate: errorThreshold, deliveryRate: deliveryThreshold }) }); }
    catch { /* error handled by apiFetch */ }
    finally { setIsSavingThresholds(false); }
  };

  const sortIndicator = (key: SortKey) => (sortKey !== key ? "" : sortDir === "asc" ? " \u25B2" : " \u25BC");

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Channel health</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitor delivery rates, errors, and performance across providers
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1">
          {TIME_RANGES.map((tr) => (
            <button
              key={tr.value}
              onClick={() => setTimeRange(tr.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                timeRange === tr.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tr.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading health data...</div>
      ) : !data || data.providers.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">No provider data available</div>
      ) : (
        <>
          {/* Provider health cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((provider) => (
              <div key={provider.id} className="rounded-md border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">{provider.name}</h3>
                  <span className="text-xs text-muted-foreground">{provider.channel}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Delivery</p>
                    <p className={`font-semibold ${rateColor(provider.deliveryRate, true)}`}>
                      {provider.deliveryRate.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Errors</p>
                    <p className={`font-semibold ${rateColor(provider.errorRate, false)}`}>
                      {provider.errorRate.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Avg time</p>
                    <p className="font-semibold text-foreground">{provider.avgDeliveryTime.toFixed(1)}s</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Sent (24h)</p>
                    <p className="font-semibold text-foreground">{provider.messagesSent.toLocaleString()}</p>
                  </div>
                </div>
                {provider.hourlyBars.length > 0 && <BarChart bars={provider.hourlyBars} />}
                {provider.lastError && (
                  <div className="rounded bg-[var(--color-destructive)]/5 px-2 py-1.5 text-xs text-[var(--color-destructive)]">
                    <span className="font-medium">{formatTime(provider.lastError.timestamp)}</span>
                    {" \u2014 "}
                    {provider.lastError.message}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Comparison table */}
          <div>
            <h3 className="mb-3 text-sm font-medium text-foreground">Provider comparison</h3>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {([
                      ["name", "Provider"],
                      ["deliveryRate", "Delivery %"],
                      ["errorRate", "Error %"],
                      ["avgDeliveryTime", "Avg time (s)"],
                      ["messagesSent", "Sent (24h)"],
                    ] as [SortKey, string][]).map(([key, label]) => (
                      <th
                        key={key}
                        onClick={() => toggleSort(key)}
                        className="cursor-pointer px-4 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
                      >
                        {label}{sortIndicator(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-medium text-foreground">{p.name}</td>
                      <td className={`px-4 py-2 ${rateColor(p.deliveryRate, true)}`}>
                        {p.deliveryRate.toFixed(1)}%
                      </td>
                      <td className={`px-4 py-2 ${rateColor(p.errorRate, false)}`}>
                        {p.errorRate.toFixed(1)}%
                      </td>
                      <td className="px-4 py-2 text-foreground">{p.avgDeliveryTime.toFixed(1)}</td>
                      <td className="px-4 py-2 text-foreground">{p.messagesSent.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Alert thresholds */}
          <div className="space-y-4 rounded-md border border-border p-4">
            <h3 className="text-sm font-medium text-foreground">Alert thresholds</h3>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                Alert when error rate exceeds
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={errorThreshold}
                  onChange={(e) => setErrorThreshold(Math.max(1, Number(e.target.value)))}
                  className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                />
                %
              </label>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                Alert when delivery rate drops below
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={deliveryThreshold}
                  onChange={(e) => setDeliveryThreshold(Math.max(1, Number(e.target.value)))}
                  className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                />
                %
              </label>
              <button
                onClick={saveThresholds}
                disabled={isSavingThresholds}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {isSavingThresholds ? "Saving..." : "Save thresholds"}
              </button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">Auto-refreshes every 60 seconds</p>
        </>
      )}
    </div>
  );
}
